import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, type KnowledgeItemData } from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import { getBackendMessage } from "../lib/executiveMemos";
import { saveGroupTalkDraft } from "../lib/groupTalkSessionStorage";
import { buildKnowledgeWallTalkDraft } from "../lib/knowledgeGroupTalkDraft";
import {
  canRetryKnowledgeItemAiPreparation,
  getKnowledgeAiPreparationFailureMessage,
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeLibraryStatusLabel,
  getKnowledgeSummaryStatusLabel,
  getKnowledgeTypeLabel,
  getKnowledgeUnavailableReason,
  isKnowledgeItemLibraryAvailable,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemAiPreparationFailed,
  isKnowledgeItemPending,
  isKnowledgeItemPollingStatus,
  isKnowledgeItemUsable,
  shouldShowKnowledgeSummaryStatus,
  validateKnowledgeUrlInput,
} from "../lib/knowledge";

const KNOWLEDGE_LIBRARY_TYPES = "pdf,executive_memo,url";
const KNOWLEDGE_LIBRARY_LIMIT = 20;
const KNOWLEDGE_POLLING_INTERVAL_MS = 5000;
const KNOWLEDGE_HIDDEN_POLLING_INTERVAL_MS = 15000;
const MAX_URL_POLLING_ATTEMPTS = 60;

type ItemMutation = "renaming" | "reprocessing" | "retrying" | "syncing" | "deleting";
type Notification = { type: "success" | "error"; message: string };
type KnowledgeUrlOperation = "register" | "retry" | "rename" | "delete";
type KnowledgeLibraryReadOperation = "load" | "load_more" | "poll";
type KnowledgePollResult = {
  itemId: string;
  item: KnowledgeItemData | null;
  missing: boolean;
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function replaceKnowledgeItem(
  items: KnowledgeItemData[],
  updatedItem: KnowledgeItemData,
  previousId = updatedItem.id
): KnowledgeItemData[] {
  const targetIndex = items.findIndex((item) => item.id === previousId || item.id === updatedItem.id);
  const withoutTarget = items.filter((item) => item.id !== previousId && item.id !== updatedItem.id);
  if (targetIndex < 0) return [updatedItem, ...withoutTarget];
  return [...withoutTarget.slice(0, targetIndex), updatedItem, ...withoutTarget.slice(targetIndex)];
}

function appendUniqueKnowledgeItems(current: KnowledgeItemData[], incoming: KnowledgeItemData[]): KnowledgeItemData[] {
  const knownIds = new Set(current.map((item) => item.id));
  return [...current, ...incoming.filter((item) => !knownIds.has(item.id))];
}

function mergeRefreshedKnowledgeItems(
  current: KnowledgeItemData[],
  incoming: KnowledgeItemData[],
  activeMutations: Record<string, ItemMutation>
): KnowledgeItemData[] {
  const currentById = new Map(current.map((item) => [item.id, item]));
  const protectedIncoming = incoming.map((item) => activeMutations[item.id] ? currentById.get(item.id) ?? item : item);
  const refreshedIds = new Set(protectedIncoming.map((item) => item.id));
  return [...protectedIncoming, ...current.filter((item) => !refreshedIds.has(item.id))];
}

function knowledgeMutationError(error: unknown, fallback: string, fields: string[] = []): string {
  return getBackendMessage(error, fields) || `${toUserMessage(error)} ${fallback}`;
}

function knowledgeUrlOperationError(error: unknown, operation: KnowledgeUrlOperation): string {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  if (operation === "register") {
    if (code === "CONFLICT") return "このURLは既にナレッジライブラリへ登録されています。";
    if (code === "BAD_REQUEST") return "URLを追加できませんでした。入力内容を確認して、もう一度お試しください。";
    if (code === "NETWORK" || code === "TIMEOUT") return "通信に失敗しました。ネットワーク状況を確認して、もう一度お試しください。";
    return "URLを追加できませんでした。入力内容を確認して、もう一度お試しください。";
  }
  if (operation === "retry") {
    if (code === "CONFLICT" || code === "BAD_REQUEST") return "このWebページは現在再試行できません。";
    return "再試行を開始できませんでした。時間をおいて、もう一度お試しください。";
  }
  if (operation === "rename") return "Webページの資料名を変更できませんでした。";
  return "Webページを削除できませんでした。時間をおいて、もう一度お試しください。";
}

function logKnowledgeUrlOperationError(error: unknown, operation: KnowledgeUrlOperation): void {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  const status = error instanceof AppError ? error.meta.status : undefined;
  logError(new Error(`knowledge_url_${operation}_failed`), {
    action: `knowledge_url_${operation}`,
    errorCode: code,
    statusClass: typeof status === "number" ? `${Math.floor(status / 100)}xx` : undefined,
  });
}

function logKnowledgeLibraryReadError(error: unknown, operation: KnowledgeLibraryReadOperation): void {
  const code = error instanceof AppError ? error.code : "UNKNOWN";
  const status = error instanceof AppError ? error.meta.status : undefined;
  logError(new Error(`knowledge_library_${operation}_failed`), {
    action: `knowledge_library_${operation}`,
    errorCode: code,
    statusClass: typeof status === "number" ? `${Math.floor(status / 100)}xx` : undefined,
  });
}

function hasKnowledgeTitleControlCharacter(value: string): boolean {
  return /\p{C}/u.test(value);
}

function normalizeKnowledgeTitle(value: string): { title: string; error: string } {
  if (hasKnowledgeTitleControlCharacter(value)) {
    return { title: value, error: "資料名に改行、タブ、制御文字は使用できません。" };
  }
  const title = value.trim();
  if (!title) return { title, error: "資料名を入力してください。" };
  if (Array.from(title).length > 255) return { title, error: "資料名は255文字以内で入力してください。" };
  return { title, error: "" };
}

function KnowledgeMetadata({ item, displayName }: { item: KnowledgeItemData; displayName: string }) {
  const createdAt = formatDateTime(item.created_at);
  const updatedAt = formatDateTime(item.updated_at);
  const summarizedAt = formatDateTime(item.summarized_at);
  const fetchedAt = formatDateTime(item.fetched_at);
  const summaryLabel = getKnowledgeSummaryStatusLabel(item);
  const showSummaryStatus = shouldShowKnowledgeSummaryStatus(item);
  const showOriginalFilename = Boolean(
    item.type !== "url" && item.original_filename && item.original_filename !== displayName
  );

  return (
    <dl className="mt-4 grid min-w-0 gap-2 text-sm text-[#6B5851] sm:grid-cols-2">
      {showOriginalFilename ? (
        <div className="min-w-0">
          <dt className="text-xs font-black text-[#A67C52]">元ファイル名</dt>
          <dd className="mt-1 break-words font-bold [overflow-wrap:anywhere]">{item.original_filename}</dd>
        </div>
      ) : null}
      {item.type !== "url" && item.page_count ? (
        <div>
          <dt className="text-xs font-black text-[#A67C52]">ページ数</dt>
          <dd className="mt-1 font-bold">{item.page_count}ページ</dd>
        </div>
      ) : null}
      {item.type === "url" && item.domain ? (
        <div className="min-w-0">
          <dt className="text-xs font-black text-[#A67C52]">ドメイン</dt>
          <dd className="mt-1 break-words font-bold [overflow-wrap:anywhere]">{item.domain}</dd>
        </div>
      ) : null}
      {item.type === "url" && fetchedAt ? (
        <div>
          <dt className="text-xs font-black text-[#A67C52]">取得</dt>
          <dd className="mt-1 font-bold">{fetchedAt}</dd>
        </div>
      ) : null}
      {showSummaryStatus ? (
        <div>
          <dt className="text-xs font-black text-[#A67C52]">要約</dt>
          <dd className="mt-1 font-bold">{summarizedAt ? `${summarizedAt} ${summaryLabel}` : summaryLabel}</dd>
        </div>
      ) : null}
      {createdAt ? (
        <div>
          <dt className="text-xs font-black text-[#A67C52]">作成</dt>
          <dd className="mt-1 font-bold">{createdAt}</dd>
        </div>
      ) : null}
      {updatedAt ? (
        <div>
          <dt className="text-xs font-black text-[#A67C52]">更新</dt>
          <dd className="mt-1 font-bold">{updatedAt}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function KnowledgeItemCard({
  item,
  mutation,
  pollingTimedOut,
  onStartWallTalk,
  onRequestRename,
  onRequestReprocess,
  onRetryUrl,
  onRequestDelete,
  onRefreshFromExecutiveMemo,
}: {
  item: KnowledgeItemData;
  mutation?: ItemMutation;
  pollingTimedOut?: boolean;
  onStartWallTalk: (item: KnowledgeItemData) => void;
  onRequestRename: (item: KnowledgeItemData) => void;
  onRequestReprocess: (item: KnowledgeItemData) => void;
  onRetryUrl: (item: KnowledgeItemData) => void;
  onRequestDelete: (item: KnowledgeItemData) => void;
  onRefreshFromExecutiveMemo: (item: KnowledgeItemData) => void;
}) {
  const displayName = getKnowledgeItemDisplayName(item);
  const usable = isKnowledgeItemUsable(item);
  const libraryAvailable = isKnowledgeItemLibraryAvailable(item);
  const groupTalkUsable = isKnowledgeItemGroupTalkUsable(item);
  const unavailableReason = item.type === "url" ? "" : getKnowledgeUnavailableReason(item);
  const mutating = Boolean(mutation);
  const canReprocessPdf = item.type === "pdf" && item.status === "failed" && item.can_retry;
  const canRetryUrl = item.type === "url" && item.status === "failed" && item.can_retry;
  const canRetryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
  const canRefreshFromExecutiveMemo = item.type === "executive_memo" && item.status === "failed" && Boolean(item.source_id);

  return (
    <article className="max-w-full overflow-hidden rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="shrink-0 rounded-full bg-[#FFF3E9] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
              種別: {getKnowledgeTypeLabel(item.type)}
            </span>
            <span className="shrink-0 rounded-full bg-[#F5EEE7] px-3 py-1 text-[11px] font-black text-[#7C5A4E]">
              状態: {getKnowledgeLibraryStatusLabel(item)}
            </span>
            {shouldShowKnowledgeSummaryStatus(item) ? (
              <span className="shrink-0 rounded-full bg-[#F7F5ED] px-3 py-1 text-[11px] font-black text-[#6F7462]">
                {getKnowledgeSummaryStatusLabel(item)}
              </span>
            ) : null}
          </div>
          <h2 className="mt-3 max-w-full break-words text-xl font-black text-[#5D4D46] [overflow-wrap:anywhere]">
            {displayName}
          </h2>
          <KnowledgeMetadata item={item} displayName={displayName} />
          {isKnowledgeItemAiPreparationFailed(item) ? (
            <div className="mt-4 max-w-full rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-3 py-2">
              <p className="text-xs font-black text-[#A45645]">準備に失敗</p>
              <p className="mt-1 break-words text-sm font-bold text-[#C06D5A] [overflow-wrap:anywhere]">
                {getKnowledgeAiPreparationFailureMessage(item.ai_preparation_failure_code)}
              </p>
            </div>
          ) : item.status === "failed" ? (
            <div className="mt-4 max-w-full rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-3 py-2">
              <p className="text-xs font-black text-[#A45645]">処理に失敗</p>
              <p className="mt-1 break-words text-sm font-bold text-[#C06D5A] [overflow-wrap:anywhere]">
                {getKnowledgeFailureDisplayMessage(item)}
              </p>
            </div>
          ) : null}
          {item.type === "url" && pollingTimedOut && isKnowledgeItemPending(item) ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              準備に時間がかかっています。しばらくしてから画面を再読み込みして状態を確認してください。
            </p>
          ) : item.type === "url" && isKnowledgeItemPollingStatus(item.status) ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              Webページの内容を読み取っています
            </p>
          ) : item.type === "url" && item.ai_preparation_status === "processing" ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              AIで利用するための準備をしています。
            </p>
          ) : item.type === "url" && item.status === "ready" && !libraryAvailable
            && !isKnowledgeItemAiPreparationFailed(item) ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              AIで利用するための準備が完了していません。
            </p>
          ) : null}
          {item.type !== "url" && !usable && item.status !== "failed" ? (
            <p className="mt-4 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              {unavailableReason}
            </p>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto sm:max-w-[18rem] sm:justify-end">
          <Link
            to={`/request/knowledge/${encodeURIComponent(item.id)}`}
            className="inline-flex min-h-[44px] max-w-full items-center justify-center whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
          >
            詳細を見る
          </Link>
          {(item.type === "pdf" || item.type === "executive_memo"
            || (item.type === "url" && libraryAvailable && !pollingTimedOut)) ? (
            <button
              type="button"
              aria-label={`このナレッジで壁打ちする ${displayName}`}
              onClick={() => onStartWallTalk(item)}
              disabled={!groupTalkUsable || mutating}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full bg-[#5D4D46] px-5 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:bg-[#B9AAA2] disabled:opacity-70"
            >
              このナレッジで壁打ちする
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onRequestRename(item)}
            disabled={mutating}
            className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation === "renaming" ? "保存中..." : "名前を変更"}
          </button>
          {canReprocessPdf ? (
            <button
              type="button"
              onClick={() => onRequestReprocess(item)}
              disabled={mutating}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#C9B7A7] bg-[#FFFDFB] px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "reprocessing" ? "開始中..." : "PDFをもう一度処理"}
            </button>
          ) : null}
          {canRetryUrl || canRetryAiPreparation ? (
            <button
              type="button"
              onClick={() => onRetryUrl(item)}
              disabled={mutating}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#C9B7A7] bg-[#FFFDFB] px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "retrying" ? "開始中..." : canRetryAiPreparation ? "要約を再試行" : "再試行"}
            </button>
          ) : null}
          {canRefreshFromExecutiveMemo ? (
            <button
              type="button"
              onClick={() => onRefreshFromExecutiveMemo(item)}
              disabled={mutating}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation === "syncing" ? "更新中..." : "社長メモから更新"}
            </button>
          ) : null}
          <button
            type="button"
            aria-label={`ナレッジを削除 ${displayName}`}
            onClick={() => onRequestDelete(item)}
            disabled={mutating}
            className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#E2B9A9] bg-white px-5 py-2 text-sm font-black text-[#A45645] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation === "deleting" ? "削除中..." : "削除"}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function KnowledgeLibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<KnowledgeItemData[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const urlSubmissionRef = useRef(false);
  const [mutations, setMutations] = useState<Record<string, ItemMutation>>({});
  const mutationRef = useRef<Record<string, ItemMutation>>({});
  const itemRevisionRef = useRef<Record<string, number>>({});
  const urlPollingAttemptRef = useRef<Record<string, number>>({});
  const [timedOutUrlItemIds, setTimedOutUrlItemIds] = useState<Record<string, true>>({});
  const loadedAdditionalPagesRef = useRef(false);
  const [renameTarget, setRenameTarget] = useState<KnowledgeItemData | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renameError, setRenameError] = useState("");
  const [reprocessTarget, setReprocessTarget] = useState<KnowledgeItemData | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeItemData | null>(null);

  const pendingItemKey = useMemo(
    () => items
      .filter((item) => isKnowledgeItemPending(item) && !(item.type === "url" && timedOutUrlItemIds[item.id]))
      .map((item) => item.id)
      .sort()
      .join("\u001f"),
    [items, timedOutUrlItemIds]
  );
  const pendingUrlItemKey = useMemo(
    () => items
      .filter((item) => item.type === "url" && isKnowledgeItemPending(item) && !timedOutUrlItemIds[item.id])
      .map((item) => item.id)
      .sort()
      .join("\u001f"),
    [items, timedOutUrlItemIds]
  );
  const clearUrlPollingState = useCallback((itemIds: readonly string[]) => {
    if (itemIds.length === 0) return;
    itemIds.forEach((itemId) => {
      delete urlPollingAttemptRef.current[itemId];
    });
    setTimedOutUrlItemIds((current) => {
      const next = { ...current };
      let changed = false;
      itemIds.forEach((itemId) => {
        if (!next[itemId]) return;
        delete next[itemId];
        changed = true;
      });
      return changed ? next : current;
    });
  }, []);

  const markUrlPollingTimedOut = useCallback((itemIds: readonly string[]) => {
    if (itemIds.length === 0) return;
    setTimedOutUrlItemIds((current) => {
      const next = { ...current };
      let changed = false;
      itemIds.forEach((itemId) => {
        if (next[itemId]) return;
        next[itemId] = true;
        changed = true;
      });
      return changed ? next : current;
    });
  }, []);

  const showNotification = (type: Notification["type"], message: string) => {
    setNotification({ type, message });
  };

  const incrementItemRevision = (itemId: string) => {
    itemRevisionRef.current[itemId] = (itemRevisionRef.current[itemId] ?? 0) + 1;
  };

  const startMutation = (itemId: string, mutation: ItemMutation): boolean => {
    if (mutationRef.current[itemId]) return false;
    incrementItemRevision(itemId);
    mutationRef.current = { ...mutationRef.current, [itemId]: mutation };
    setMutations(mutationRef.current);
    return true;
  };

  const finishMutation = (itemId: string, mutation: ItemMutation) => {
    if (mutationRef.current[itemId] !== mutation) return;
    incrementItemRevision(itemId);
    const next = { ...mutationRef.current };
    delete next[itemId];
    mutationRef.current = next;
    setMutations(next);
  };

  const refreshFirstPage = async (): Promise<void> => {
    const response = await api.listKnowledgeItems({
      type: KNOWLEDGE_LIBRARY_TYPES,
      limit: KNOWLEDGE_LIBRARY_LIMIT,
    });
    setItems((current) => mergeRefreshedKnowledgeItems(current, response.items, mutationRef.current));
    if (!loadedAdditionalPagesRef.current) setNextCursor(response.next_cursor ?? null);
  };

  const submitUrl = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (urlSubmissionRef.current) return;
    const validation = validateKnowledgeUrlInput(urlInput);
    if (validation.error) {
      setUrlError(validation.error);
      return;
    }

    urlSubmissionRef.current = true;
    setUrlSubmitting(true);
    setUrlError("");
    try {
      const createdItem = await api.createKnowledgeItemFromUrl(validation.url);
      clearUrlPollingState([createdItem.id]);
      setItems((current) => replaceKnowledgeItem(current, createdItem));
      setUrlInput("");
      showNotification("success", "URLを追加しました。内容を読み取っています。");
      try {
        await refreshFirstPage();
      } catch (refreshError) {
        logKnowledgeUrlOperationError(refreshError, "register");
      }
    } catch (error) {
      logKnowledgeUrlOperationError(error, "register");
      showNotification("error", knowledgeUrlOperationError(error, "register"));
    } finally {
      urlSubmissionRef.current = false;
      setUrlSubmitting(false);
    }
  };

  useEffect(() => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setLoading(true);
    setNotification(null);
    loadedAdditionalPagesRef.current = false;

    api.listKnowledgeItems(
      { type: KNOWLEDGE_LIBRARY_TYPES, limit: KNOWLEDGE_LIBRARY_LIMIT },
      controller?.signal
    )
      .then((response) => {
        setItems(response.items);
        setNextCursor(response.next_cursor ?? null);
      })
      .catch((error) => {
        if (controller?.signal.aborted) return;
        logKnowledgeLibraryReadError(error, "load");
        showNotification("error", "ナレッジライブラリを読み込めませんでした。時間をおいて、もう一度お試しください。");
      })
      .finally(() => {
        if (!controller?.signal.aborted) setLoading(false);
      });

    return () => controller?.abort();
  }, []);

  useEffect(() => {
    if (!notification) return;
    const timeout = window.setTimeout(
      () => setNotification(null),
      notification.type === "success" ? 5000 : 8000
    );
    return () => window.clearTimeout(timeout);
  }, [notification]);

  useEffect(() => {
    const activePendingUrlIds = new Set(
      items
        .filter((item) => item.type === "url" && isKnowledgeItemPending(item))
        .map((item) => item.id)
    );
    const staleItemIds = Object.keys(urlPollingAttemptRef.current)
      .filter((itemId) => !activePendingUrlIds.has(itemId));
    if (staleItemIds.length > 0) clearUrlPollingState(staleItemIds);
  }, [clearUrlPollingState, items]);

  useEffect(() => {
    if (!pendingItemKey) return;
    const itemIds = pendingItemKey.split("\u001f");
    const urlItemIds = new Set(pendingUrlItemKey ? pendingUrlItemKey.split("\u001f") : []);
    let stopped = false;
    let timeout: number | null = null;
    let controller: AbortController | null = null;

    const scheduleNextPoll = () => {
      if (stopped) return;
      const interval = typeof document !== "undefined" && document.visibilityState === "hidden"
        ? KNOWLEDGE_HIDDEN_POLLING_INTERVAL_MS
        : KNOWLEDGE_POLLING_INTERVAL_MS;
      timeout = window.setTimeout(() => void poll(), interval);
    };

    const poll = async () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        scheduleNextPoll();
        return;
      }

      const alreadyCappedIds: string[] = [];
      const requestItemIds = itemIds.filter((itemId) => {
        if (!urlItemIds.has(itemId)) return true;
        const attempts = urlPollingAttemptRef.current[itemId] ?? 0;
        if (attempts >= MAX_URL_POLLING_ATTEMPTS) {
          alreadyCappedIds.push(itemId);
          return false;
        }
        urlPollingAttemptRef.current[itemId] = attempts + 1;
        return true;
      });
      markUrlPollingTimedOut(alreadyCappedIds);
      if (requestItemIds.length === 0) return;

      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const revisionSnapshot: Record<string, number> = {};
      requestItemIds.forEach((itemId) => {
        revisionSnapshot[itemId] = itemRevisionRef.current[itemId] ?? 0;
      });
      const pollResults: KnowledgePollResult[] = await Promise.all(requestItemIds.map(async (itemId) => {
        try {
          return {
            itemId,
            item: await api.getKnowledgeItem(itemId, controller?.signal),
            missing: false,
          };
        } catch (error) {
          const missing = error instanceof AppError && error.code === "NOT_FOUND";
          if (!controller?.signal.aborted && !missing) logKnowledgeLibraryReadError(error, "poll");
          return { itemId, item: null, missing };
        }
      }));
      if (stopped) return;

      const updates = new Map<string, KnowledgeItemData>();
      const removedItemIds = new Set<string>();
      const completedItemIds: string[] = [];
      const timedOutItemIds: string[] = [];

      pollResults.forEach((result) => {
        const revisionUnchanged = (itemRevisionRef.current[result.itemId] ?? 0)
          === revisionSnapshot[result.itemId];
        if (!revisionUnchanged || mutationRef.current[result.itemId]) {
          return;
        }

        if (result.missing) {
          removedItemIds.add(result.itemId);
          completedItemIds.push(result.itemId);
          return;
        }

        if (result.item) {
          updates.set(result.itemId, result.item);
          if (urlItemIds.has(result.itemId) && !isKnowledgeItemPending(result.item)) {
            completedItemIds.push(result.itemId);
            return;
          }
        }

        if (urlItemIds.has(result.itemId)
          && (urlPollingAttemptRef.current[result.itemId] ?? 0) >= MAX_URL_POLLING_ATTEMPTS) {
          timedOutItemIds.push(result.itemId);
        }
      });

      setItems((current) => current
        .filter((item) => !removedItemIds.has(item.id))
        .map((item) => updates.get(item.id) ?? item));
      clearUrlPollingState(completedItemIds);
      markUrlPollingTimedOut(timedOutItemIds);
      scheduleNextPoll();
    };

    scheduleNextPoll();
    return () => {
      stopped = true;
      if (timeout !== null) window.clearTimeout(timeout);
      controller?.abort();
    };
  }, [
    clearUrlPollingState,
    markUrlPollingTimedOut,
    pendingItemKey,
    pendingUrlItemKey,
  ]);

  const startWallTalk = (item: KnowledgeItemData) => {
    if (!isKnowledgeItemGroupTalkUsable(item) || mutationRef.current[item.id]) return;
    const draft = buildKnowledgeWallTalkDraft(item);
    saveGroupTalkDraft(draft);
    navigate("/talk/group", { state: { draft } });
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const cursor = nextCursor;
    setLoadingMore(true);
    try {
      const response = await api.listKnowledgeItems({
        type: KNOWLEDGE_LIBRARY_TYPES,
        limit: KNOWLEDGE_LIBRARY_LIMIT,
        cursor,
      });
      setItems((current) => appendUniqueKnowledgeItems(current, response.items));
      setNextCursor(response.next_cursor ?? null);
      loadedAdditionalPagesRef.current = true;
    } catch (error) {
      logKnowledgeLibraryReadError(error, "load_more");
      showNotification("error", "続きを読み込めませんでした。時間をおいて、もう一度お試しください。");
    } finally {
      setLoadingMore(false);
    }
  };

  const openRenameModal = (item: KnowledgeItemData) => {
    if (mutationRef.current[item.id]) return;
    setRenameTarget(item);
    setRenameTitle(getKnowledgeItemDisplayName(item));
    setRenameError("");
  };

  const saveRename = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!renameTarget) return;
    const validation = normalizeKnowledgeTitle(renameTitle);
    if (validation.error) {
      setRenameError(validation.error);
      return;
    }
    if (validation.title === getKnowledgeItemDisplayName(renameTarget)) {
      setRenameError("現在と同じ資料名です。");
      return;
    }
    if (!startMutation(renameTarget.id, "renaming")) return;
    setRenameError("");

    try {
      const updatedItem = await api.updateKnowledgeItemTitle(renameTarget.id, validation.title);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, renameTarget.id));
      setRenameTarget(null);
      showNotification("success", "資料名を変更しました。");
    } catch (error) {
      if (renameTarget.type === "url") {
        logKnowledgeUrlOperationError(error, "rename");
        setRenameError(knowledgeUrlOperationError(error, "rename"));
      } else {
        logError(error);
        setRenameError(knowledgeMutationError(error, "資料名を変更できませんでした。", ["title"]));
      }
    } finally {
      finishMutation(renameTarget.id, "renaming");
    }
  };

  const reprocessPdf = async () => {
    if (!reprocessTarget || !startMutation(reprocessTarget.id, "reprocessing")) return;
    const target = reprocessTarget;
    try {
      const updatedItem = await api.reprocessKnowledgePdf(target.id);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, target.id));
      setReprocessTarget(null);
      showNotification("success", "PDFの再処理を開始しました。");
    } catch (error) {
      logError(error);
      setReprocessTarget(null);
      showNotification("error", knowledgeMutationError(error, "PDFをもう一度処理できませんでした。"));
    } finally {
      finishMutation(target.id, "reprocessing");
    }
  };

  const retryUrl = async (item: KnowledgeItemData) => {
    const retryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
    const retryFullUrl = item.type === "url" && item.status === "failed" && item.can_retry;
    if ((!retryAiPreparation && !retryFullUrl) || !startMutation(item.id, "retrying")) return;
    try {
      const updatedItem = await api.retryKnowledgeUrl(item.id);
      clearUrlPollingState([item.id]);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, item.id));
      showNotification(
        "success",
        retryAiPreparation ? "要約の再試行を開始しました。" : "再試行を開始しました。"
      );
      if (!retryAiPreparation) {
        try {
          await refreshFirstPage();
        } catch (refreshError) {
          logKnowledgeUrlOperationError(refreshError, "retry");
        }
      }
    } catch (error) {
      logKnowledgeUrlOperationError(error, "retry");
      showNotification("error", knowledgeUrlOperationError(error, "retry"));
    } finally {
      finishMutation(item.id, "retrying");
    }
  };

  const refreshFromExecutiveMemo = async (item: KnowledgeItemData) => {
    if (item.type !== "executive_memo" || !item.source_id || !startMutation(item.id, "syncing")) return;
    try {
      const updatedItem = await api.createKnowledgeItemFromExecutiveMemo(item.source_id);
      setItems((current) => replaceKnowledgeItem(current, updatedItem, item.id));
      showNotification("success", "社長メモの内容を更新しました。");
    } catch (error) {
      logError(error);
      showNotification("error", knowledgeMutationError(error, "社長メモから更新できませんでした。"));
    } finally {
      finishMutation(item.id, "syncing");
    }
  };

  const deleteKnowledge = async () => {
    if (!deleteTarget || !startMutation(deleteTarget.id, "deleting")) return;
    const target = deleteTarget;
    try {
      await api.deleteKnowledgeItem(target.id);
      clearUrlPollingState([target.id]);
      setItems((current) => current.filter((item) => item.id !== target.id));
      setDeleteTarget(null);
      showNotification("success", "資料を削除しました。");
    } catch (error) {
      if (target.type === "url") {
        logKnowledgeUrlOperationError(error, "delete");
        showNotification("error", knowledgeUrlOperationError(error, "delete"));
      } else {
        logError(error);
        showNotification("error", knowledgeMutationError(error, "ナレッジを削除できませんでした。"));
      }
    } finally {
      finishMutation(target.id, "deleting");
    }
  };

  const renameSaving = Boolean(renameTarget && mutations[renameTarget.id] === "renaming");
  const reprocessStarting = Boolean(reprocessTarget && mutations[reprocessTarget.id] === "reprocessing");
  const deletePending = Boolean(deleteTarget && mutations[deleteTarget.id] === "deleting");

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between gap-3">
          <Link to="/request" className="shrink-0 text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">←戻る</Link>
          <p className="min-w-0 break-words text-center text-sm font-bold text-[#7C5A4E]">ナレッジライブラリ</p>
          <Link to="/home" className="shrink-0 text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">WizMate</Link>
        </header>

        <section className="border-b border-[#EADBC8] pb-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">KNOWLEDGE LIBRARY</p>
          <h1 className="mt-3 break-words text-3xl font-black text-[#5D4D46] sm:text-4xl">ナレッジライブラリ</h1>
          <p className="mt-3 max-w-2xl break-words text-sm leading-7 text-[#6B5851]">
            PDFやWebページ、社長メモをナレッジライブラリで管理できます。
          </p>
        </section>

        {notification ? (
          <div
            role={notification.type === "error" ? "alert" : "status"}
            className={`mt-5 flex max-w-full items-start justify-between gap-3 rounded-[1rem] border px-4 py-3 text-sm font-bold ${
              notification.type === "error"
                ? "border-[#F0C8BA] bg-[#FFF7F1] text-[#C06D5A]"
                : "border-[#BFD8C0] bg-[#F5FBF4] text-[#4B7949]"
            }`}
          >
            <span className="min-w-0 break-words [overflow-wrap:anywhere]">{notification.message}</span>
            <button
              type="button"
              aria-label="通知を閉じる"
              onClick={() => setNotification(null)}
              className="shrink-0 rounded-full px-2 py-0.5 text-base leading-none hover:bg-black/5"
            >
              ×
            </button>
          </div>
        ) : null}

        <section className="mt-6 max-w-full overflow-hidden rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm">
          <h2 className="break-words text-lg font-black text-[#5D4D46]">Webページを追加</h2>
          <p id="knowledge-url-help" className="mt-2 break-words text-sm leading-6 text-[#6B5851]">
            WebページのURLを登録すると、内容を読み取ってAIで使えるナレッジにします。ログインが必要なページや、アクセスできないページは登録できない場合があります。
          </p>
          <form onSubmit={(event) => void submitUrl(event)} className="mt-4 min-w-0" aria-busy={urlSubmitting}>
            <label htmlFor="knowledge-url-input" className="block text-sm font-black text-[#6B5851]">WebページのURL</label>
            <div className="mt-2 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-stretch">
              <input
                id="knowledge-url-input"
                type="text"
                inputMode="url"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="https://example.com/article"
                value={urlInput}
                onChange={(event) => {
                  setUrlInput(event.target.value);
                  setUrlError("");
                }}
                onPaste={(event) => {
                  const pasted = event.clipboardData.getData("text");
                  const validation = validateKnowledgeUrlInput(pasted);
                  if (!validation.error || !/制御文字/.test(validation.error)) return;
                  event.preventDefault();
                  setUrlError(validation.error);
                }}
                disabled={urlSubmitting}
                aria-invalid={urlError ? "true" : "false"}
                aria-describedby={urlError ? "knowledge-url-help knowledge-url-error" : "knowledge-url-help"}
                className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-[#D9C5B9] px-3 py-2 text-base text-[#4A3E39] outline-none focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:cursor-not-allowed disabled:bg-[#F7F2EE] disabled:opacity-70"
              />
              <button
                type="submit"
                disabled={urlSubmitting}
                className="min-h-[44px] w-full shrink-0 rounded-full bg-[#5D4D46] px-6 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {urlSubmitting ? "追加中..." : "URLを追加"}
              </button>
            </div>
            {urlError ? (
              <p id="knowledge-url-error" role="alert" className="mt-3 break-words rounded-xl bg-[#FFF0EB] px-3 py-2 text-sm font-bold text-[#B4533A] [overflow-wrap:anywhere]">
                {urlError}
              </p>
            ) : null}
            {urlSubmitting ? <p role="status" className="mt-3 text-sm font-bold text-[#8F7468]">URLを送信中です...</p> : null}
          </form>
        </section>

        <section className="mt-6">
          {loading ? (
            <p className="rounded-[1.2rem] border border-[#EADBC8] bg-white px-4 py-4 text-sm font-bold text-[#8F7468]">
              ナレッジを読み込み中です...
            </p>
          ) : items.length === 0 ? (
            <div className="rounded-[1.4rem] border border-dashed border-[#D9C5B9] bg-white px-5 py-6">
              <h2 className="break-words text-lg font-black text-[#5D4D46]">まだ表示できるナレッジがありません</h2>
              <p className="mt-2 break-words text-sm leading-6 text-[#6B5851]">
                PDFアップロード、Webページの追加、社長メモのナレッジ化が完了すると、ここに表示されます。
              </p>
            </div>
          ) : (
            <div className="grid min-w-0 gap-4">
              {items.map((item) => (
                <KnowledgeItemCard
                  key={item.id}
                  item={item}
                  mutation={mutations[item.id]}
                  pollingTimedOut={Boolean(timedOutUrlItemIds[item.id])}
                  onStartWallTalk={startWallTalk}
                  onRequestRename={openRenameModal}
                  onRequestReprocess={setReprocessTarget}
                  onRetryUrl={(target) => void retryUrl(target)}
                  onRequestDelete={setDeleteTarget}
                  onRefreshFromExecutiveMemo={(target) => void refreshFromExecutiveMemo(target)}
                />
              ))}
            </div>
          )}
          {nextCursor ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#D9C5B9] bg-white px-6 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingMore ? "読み込み中..." : "さらに読み込む"}
              </button>
            </div>
          ) : null}
        </section>
      </div>

      {renameTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-rename-title"
            onSubmit={(event) => void saveRename(event)}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-rename-title" className="break-words text-lg font-black text-[#5D4D46]">資料名を変更</h2>
            <label htmlFor="knowledge-rename-input" className="mt-4 block text-sm font-black text-[#6B5851]">資料名</label>
            <input
              id="knowledge-rename-input"
              type="text"
              value={renameTitle}
              onChange={(event) => {
                setRenameTitle(event.target.value);
                setRenameError("");
              }}
              onPaste={(event) => {
                if (!hasKnowledgeTitleControlCharacter(event.clipboardData.getData("text"))) return;
                event.preventDefault();
                setRenameError("資料名に改行、タブ、制御文字は使用できません。");
              }}
              disabled={renameSaving}
              autoFocus
              className="mt-2 w-full max-w-full rounded-xl border border-[#D9C5B9] px-3 py-2 text-base text-[#4A3E39] outline-none focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:opacity-60"
            />
            <p className="mt-2 text-xs font-bold text-[#8F7468]">255文字以内</p>
            {renameError ? (
              <p role="alert" className="mt-3 break-words rounded-xl bg-[#FFF0EB] px-3 py-2 text-sm font-bold text-[#B4533A] [overflow-wrap:anywhere]">
                {renameError}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRenameTarget(null)}
                disabled={renameSaving}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={renameSaving}
                className="min-h-[42px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {renameSaving ? "保存中..." : "保存"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {reprocessTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-reprocess-title"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-reprocess-title" className="break-words text-lg font-black text-[#5D4D46]">PDFをもう一度処理しますか？</h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              「{getKnowledgeItemDisplayName(reprocessTarget)}」をもう一度処理します。現在の抽出結果と要約は作り直されます。
            </p>
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setReprocessTarget(null)}
                disabled={reprocessStarting}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void reprocessPdf()}
                disabled={reprocessStarting}
                className="min-h-[42px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {reprocessStarting ? "開始中..." : "もう一度処理"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5 sm:px-4">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-delete-title"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-delete-title" className="break-words text-lg font-black text-[#5D4D46]">
              {deleteTarget.type === "url" ? "このWebページをナレッジライブラリから削除しますか？" : "ナレッジライブラリから削除しますか？"}
            </h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              {deleteTarget.type === "url"
                ? `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。この操作は取り消せません。`
                : `「${getKnowledgeItemDisplayName(deleteTarget)}」をナレッジライブラリから削除します。元の社長メモやPDFの元データは削除されません。`}
            </p>
            <div className="mt-5 flex flex-col-reverse flex-wrap gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deletePending}
                className="min-h-[42px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void deleteKnowledge()}
                disabled={deletePending}
                className="min-h-[42px] rounded-full bg-[#A45645] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {deletePending ? "削除中..." : "ライブラリから削除"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
